import React from "react";
import {Button, ToggleButton, ToggleButtonGroup, Tooltip} from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import {useTranslation} from "react-i18next";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import type {Media} from "crqlar-react-common/dist/models/common/media.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {
    clampPrintoutImageHeightMm,
    clampPrintoutImageWidthMm,
    getPrintoutImageAttrs,
    MAX_PRINTOUT_IMAGE_HEIGHT_MM,
    MAX_PRINTOUT_IMAGE_WIDTH_MM,
    MIN_PRINTOUT_IMAGE_HEIGHT_MM,
    MIN_PRINTOUT_IMAGE_WIDTH_MM,
    PRINTOUT_IMAGE_ALIGNMENTS,
    PRINTOUT_IMAGE_FITS,
} from "../../../blocks/image/printoutImageOptions";
import type {
    PrintoutImageAlignment,
    PrintoutImageFit,
    PrintoutImageNodeAttrs,
} from "../../../blocks/image/printoutImageOptions";
import {PanelField} from "../PrintoutPanelSection";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

const ALIGNMENT_ICONS: Record<PrintoutImageAlignment, React.ReactNode> = {
    center: <FormatAlignCenterIcon fontSize="small"/>,
    left: <FormatAlignLeftIcon fontSize="small"/>,
    right: <FormatAlignRightIcon fontSize="small"/>,
};

const FIT_LABELS: Record<PrintoutImageFit, string> = {
    contain: "Fit",
    cover: "Fill the frame",
};

export function ImageSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const organization = useOrganization();

    const attrs = getPrintoutImageAttrs(block.node.attrs as PrintoutImageNodeAttrs);

    // Newer organizations store the logo in media; logo remains the fallback.
    const organizationLogo: Media | undefined = organization?.media?.mediaURI
        ? organization.media
        : organization?.logo?.mediaURI
            ? organization.logo
            : undefined;

    const update = (next: Record<string, unknown>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, next);

    return (
        <>
            <ImageUploadField
                compact
                disabled={disabled}
                image={attrs.src ? {mediaURI: attrs.src, publicId: attrs.publicId ?? undefined} : undefined}
                setImage={(media?: Media) => update({
                    publicId: media?.publicId ?? null,
                    src: media?.mediaURI ?? null,
                })}
                width={'100%'}
            />

            {/* Disabled MUI buttons need a wrapper for tooltip events. */}
            <Tooltip
                title={organizationLogo ? '' : t('This property has no logo uploaded yet')}
            >
                <span>
                    <Button
                        disabled={disabled || !organizationLogo}
                        fullWidth
                        onClick={() => update({
                            publicId: organizationLogo?.publicId ?? null,
                            src: organizationLogo?.mediaURI ?? null,
                        })}
                        size={'small'}
                        startIcon={<ApartmentOutlinedIcon/>}
                        sx={{textTransform: 'none'}}
                        variant={'outlined'}
                    >
                        {t('Use organization logo')}
                    </Button>
                </span>
            </Tooltip>

            <PanelField
                label={t('Width mm')}
                hint={t('Width of the image frame on the printed page, in millimetres. Limited by the available page or container column width.')}
            >
                <CNumberField
                    disabled={disabled}
                    fullWidth
                    max={MAX_PRINTOUT_IMAGE_WIDTH_MM}
                    min={MIN_PRINTOUT_IMAGE_WIDTH_MM}
                    onChange={(next) => {
                        if (next !== null) update({widthMm: clampPrintoutImageWidthMm(next)});
                    }}
                    size={'small'}
                    step={1}
                    value={attrs.widthMm}
                />
            </PanelField>

            <PanelField
                label={t('Height mm')}
                hint={t('Height of the image frame on the printed page, in millimetres. Fit controls how the image fills this space.')}
            >
                <CNumberField
                    disabled={disabled}
                    fullWidth
                    max={MAX_PRINTOUT_IMAGE_HEIGHT_MM}
                    min={MIN_PRINTOUT_IMAGE_HEIGHT_MM}
                    onChange={(next) => {
                        if (next !== null) update({heightMm: clampPrintoutImageHeightMm(next)});
                    }}
                    size={'small'}
                    step={1}
                    value={attrs.heightMm}
                />
            </PanelField>

            <PanelField label={t('Alignment')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, alignment: PrintoutImageAlignment | null) => {
                        if (alignment) update({alignment});
                    }}
                    size={'small'}
                    value={attrs.alignment}
                >
                    {PRINTOUT_IMAGE_ALIGNMENTS.map(alignment => (
                        <ToggleButton aria-label={t(alignment)} key={alignment} value={alignment}>
                            {ALIGNMENT_ICONS[alignment]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>

            <PanelField
                label={t('Fit')}
                hint={t('Fit shows the whole image and may leave empty space. Fill covers the frame and crops any excess. The frame size stays the same.')}
            >
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, fit: PrintoutImageFit | null) => {
                        if (fit) update({fit});
                    }}
                    size={'small'}
                    value={attrs.fit}
                >
                    {PRINTOUT_IMAGE_FITS.map(fit => (
                        <ToggleButton key={fit} sx={{textTransform: 'none'}} value={fit}>
                            {t(FIT_LABELS[fit])}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>
        </>
    );
}

export default ImageSettings;
